/*
 * Phones: "Sort By" / "Filter" bar fixed to the bottom of collection pages
 * (snippets/collection-mobile-bar.liquid).
 *
 * - Each button opens a sheet: overlay, scroll lock, Esc / overlay / swipe
 *   down on its top area to close, Tab kept inside, focus returned on close.
 * - The phone's back button / gesture closes an open sheet instead of leaving
 *   the page: opening adds a history entry, closing removes it again.
 * - Sort: picking an option applies it at once and closes the sheet.
 * - Filter: group tabs on the left, options on the right, with a search box
 *   that narrows a group's options in the page. "Apply" applies the ticks and
 *   closes the panel; "Reset" clears them.
 * - Applying goes through the theme's facet form (assets/facets.js), which
 *   re-renders the grid, chips and URL. This element then re-renders itself
 *   from the same response, so the bar's labels and counts stay current.
 */
(function () {
  var SWIPE_CLOSE = 80; // px dragged down before a sheet closes
  var LOCK_CLASS = 'mfacets-locked';

  if (!window.customElements || customElements.get('mobile-facets')) return;

  customElements.define(
    'mobile-facets',
    class MobileFacets extends HTMLElement {
      connectedCallback() {
        this.openSheet = null;
        this.opener = null;
        this.historyPushed = false;
        this.ignorePop = false;
        this.afterBack = null;

        this.onKeydown = this.onKeydown.bind(this);
        this.onPopState = this.onPopState.bind(this);
        window.addEventListener('popstate', this.onPopState);
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
        // Enter in a search / price box must not submit the form (a full page load).
        this.addEventListener('submit', function (event) {
          event.preventDefault();
        });
        this.addEventListener('keydown', this.onTabKeydown.bind(this));
        this.setUpSwipe();

        if (window.FoxTheme && FoxTheme.pubsub) {
          this.unsubscribe = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
            this.onFacetUpdate.bind(this)
          );
        }
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        window.removeEventListener('popstate', this.onPopState);
        document.documentElement.classList.remove(LOCK_CLASS);
      }

      // --- Open / close ---

      open(sheet, opener, viaKeyboard) {
        if (this.openSheet) this.close(false);
        this.openSheet = sheet;
        this.opener = opener;
        sheet.hidden = false;
        opener.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add(LOCK_CLASS);
        requestAnimationFrame(function () {
          sheet.classList.add('is-open');
        });

        // Keyboard: focus the chosen option. Tap: focus the title, so no focus
        // ring appears on an option the user hasn't touched yet.
        var focusTarget =
          (viaKeyboard && sheet.querySelector('[data-sort]:checked')) ||
          (viaKeyboard && sheet.querySelector('[data-tab][aria-selected="true"]')) ||
          sheet.querySelector('.mfacets-sheet__title');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
        document.addEventListener('keydown', this.onKeydown);

        // So the back button closes the sheet (same URL, no reload).
        history.pushState({ mfacetsSheet: true }, '');
        this.historyPushed = true;
      }

      // `after` runs once the sheet's history entry is gone, so a sort or filter
      // URL is written to the page's own entry, not the one Back removes.
      close(returnFocus, after) {
        var sheet = this.openSheet;
        if (!sheet) return;
        this.openSheet = null;
        sheet.classList.remove('is-open');
        var panel = sheet.querySelector('[data-sheet-panel]');
        panel.style.transform = '';
        document.documentElement.classList.remove(LOCK_CLASS);
        document.removeEventListener('keydown', this.onKeydown);
        if (this.opener) {
          this.opener.setAttribute('aria-expanded', 'false');
          if (returnFocus) this.opener.focus({ preventScroll: true });
        }

        var hide = function () {
          if (!sheet.classList.contains('is-open')) sheet.hidden = true;
        };
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) hide();
        else setTimeout(hide, 250); // after the slide-down

        if (this.historyPushed) {
          this.historyPushed = false;
          this.ignorePop = true;
          this.afterBack = after || null;
          history.back();
        } else if (after) {
          after();
        }
      }

      onPopState() {
        if (this.ignorePop) {
          // Our own history.back() from close().
          this.ignorePop = false;
          var after = this.afterBack;
          this.afterBack = null;
          if (after) after();
          return;
        }
        if (this.openSheet) {
          // The phone's back button: the entry is already gone.
          this.historyPushed = false;
          this.close(false);
        }
      }

      onClick(event) {
        var opener = event.target.closest('[data-sheet-open]');
        if (opener) {
          var sheet = document.getElementById(opener.getAttribute('aria-controls'));
          if (sheet) this.open(sheet, opener, event.detail === 0); // detail 0: Enter / Space
          return;
        }
        if (event.target.closest('[data-sheet-close]')) {
          event.preventDefault();
          event.stopPropagation();
          this.close(true);
          return;
        }
        var tab = event.target.closest('[data-tab]');
        if (tab) {
          this.selectTab(tab);
          return;
        }
        if (event.target.closest('[data-filter-clear]')) {
          this.clearFilters();
          return;
        }
        if (event.target.closest('[data-filter-apply]')) this.applyFilters();
      }

      onKeydown(event) {
        if (!this.openSheet) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close(true);
          return;
        }
        if (event.key !== 'Tab') return;
        var focusable = Array.prototype.filter.call(
          this.openSheet.querySelectorAll('button, input, [tabindex="0"]'),
          function (el) {
            return !el.disabled && el.offsetParent !== null && el.tabIndex !== -1;
          }
        );
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !this.openSheet.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      // Drag a sheet down by its top area (handle / header) to close it.
      setUpSwipe() {
        var startY = 0;
        var distance = 0;
        var panel = null;

        this.addEventListener(
          'touchstart',
          function (event) {
            var drag = event.target.closest('[data-sheet-drag]');
            if (!drag || event.target.closest('button')) return;
            panel = drag.closest('[data-sheet-panel]');
            startY = event.touches[0].clientY;
            distance = 0;
          },
          { passive: true }
        );

        this.addEventListener(
          'touchmove',
          function (event) {
            if (!panel) return;
            distance = Math.max(0, event.touches[0].clientY - startY);
            if (distance > 0) {
              event.preventDefault();
              panel.classList.add('is-dragging');
              panel.style.transform = 'translateY(' + distance + 'px)';
            }
          },
          { passive: false }
        );

        var end = function () {
          if (!panel) return;
          panel.classList.remove('is-dragging');
          if (distance > SWIPE_CLOSE) this.close(false);
          else panel.style.transform = '';
          panel = null;
        }.bind(this);
        this.addEventListener('touchend', end);
        this.addEventListener('touchcancel', end);
      }

      // --- Sort ---

      onChange(event) {
        if (event.target.matches('[data-sort]')) {
          var value = event.target.value;
          this.close(
            false,
            function () {
              var url = new URL(window.location.href);
              url.searchParams.set('sort_by', value);
              url.searchParams.delete('page');
              this.render(url);
            }.bind(this)
          );
          return;
        }
        if (event.target.matches('[data-option-search]')) return;
        if (event.target.closest('[data-filter-form]')) this.onFilterEdit();
      }

      onInput(event) {
        if (event.target.matches('[data-option-search]')) {
          this.searchOptions(event.target);
          return;
        }
        if (event.target.matches('[data-filter-form] input[type="number"]')) this.onFilterEdit();
      }

      // Show only the options in this group whose name contains the text.
      searchOptions(input) {
        var pane = input.closest('[data-pane]');
        var term = input.value.trim().toLowerCase();
        var shown = 0;
        pane.querySelectorAll('[data-option-name]').forEach(function (item) {
          var match = !term || item.dataset.optionName.indexOf(term) !== -1;
          item.hidden = !match;
          if (match) shown++;
        });
        var empty = pane.querySelector('[data-option-empty]');
        if (empty) empty.hidden = shown > 0;
      }

      // --- Filter panel ---

      get filterForm() {
        return this.querySelector('[data-filter-form]');
      }

      selectTab(tab) {
        this.querySelectorAll('[data-tab]').forEach(function (item) {
          var selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
          document.getElementById(item.getAttribute('aria-controls')).hidden = !selected;
        });
      }

      onTabKeydown(event) {
        var tab = event.target.closest('[data-tab]');
        if (!tab) return;
        var tabs = Array.prototype.slice.call(this.querySelectorAll('[data-tab]'));
        var index = tabs.indexOf(tab);
        var next = null;
        if (event.key === 'ArrowDown') next = tabs[(index + 1) % tabs.length];
        else if (event.key === 'ArrowUp') next = tabs[(index - 1 + tabs.length) % tabs.length];
        else if (event.key === 'Home') next = tabs[0];
        else if (event.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;
        event.preventDefault();
        this.selectTab(next);
        next.focus();
      }

      // Red dot on groups with something chosen.
      onFilterEdit() {
        this.querySelectorAll('[data-pane]').forEach(function (pane) {
          var chosen = Array.prototype.some.call(pane.querySelectorAll('input[name]'), function (input) {
            return input.type === 'checkbox' ? input.checked : input.value !== '';
          });
          var tab = document.getElementById(pane.getAttribute('aria-labelledby'));
          var dot = tab && tab.querySelector('[data-dot]');
          if (dot) dot.hidden = !chosen;
        });
      }

      clearFilters() {
        this.filterForm.querySelectorAll('input[name]').forEach(function (input) {
          if (input.type === 'checkbox') input.checked = false;
          else input.value = '';
        });
        this.filterForm.querySelectorAll('[data-option-search]').forEach(
          function (input) {
            input.value = '';
            this.searchOptions(input);
          }.bind(this)
        );
        this.onFilterEdit();
      }

      filterUrl() {
        var url = new URL(this.dataset.action, window.location.origin);
        new FormData(this.filterForm).forEach(function (value, key) {
          if (value !== '') url.searchParams.append(key, value);
        });
        var sortBy = new URL(window.location.href).searchParams.get('sort_by');
        if (sortBy) url.searchParams.set('sort_by', sortBy);
        return url;
      }

      applyFilters() {
        var url = this.filterUrl();
        this.close(
          false,
          function () {
            this.render(url);
          }.bind(this)
        );
      }

      // --- Shared ---

      facetForm() {
        return document.querySelector('form[is="facet-form"]');
      }

      render(url) {
        var form = this.facetForm();
        if (!form) {
          window.location.href = url.toString(); // no filters configured: plain page load
          return;
        }
        url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
        form.renderSection(url.toString(), null);
      }

      onFacetUpdate(event) {
        if (!event || !event.responseText) return;
        var doc = new DOMParser().parseFromString(event.responseText, 'text/html');
        var fresh = doc.getElementById('MobileFacets');
        if (!fresh) return;
        if (this.openSheet) this.close(false);
        this.innerHTML = fresh.innerHTML;
        this.dataset.action = fresh.dataset.action;
      }
    }
  );
})();
